import Metro from 'model/Metro';
import OnRamp from 'model/OnRamp';
import { useCallback, useEffect, useState } from 'react';
import ObjectUtils from 'utils/ObjectUtils';
import { getMapStyle, getMapStyleUrl } from 'views/components/ProjectMap/helpers/storeMapStyle';

export const defaultLayerConfiguration = {
  activeMetros: true,
  inactiveMetros: true,
  customerLocations: true,
  services: true,
  activeRegions: true,
  inactiveRegions: true,
  latencies: true,
  connections: true,
  directConnections: true,
  latencyRings: false,
  availableRegions: true,
  customerLocationLines: true,
  labelSectionEnabled: true,
  labels: true,
  metroLabels: false,
  locationLabels: false,
  regionLabels: false,
  infraComponents: true,
  capOnRampEnabled: true,
};

export const defaultCapacityConfiguration = {
  datacenters: false,
  cabe: null,
  kva: null,
  cooling: false,
  metro: 'total',
  cabinet: 'average',
};

export const useProjectMapState = ({ metrosData, onRampsData, layersData, marketplaceSegments }) => {
  const [metros, setMetros] = useState(null);
  const [connectionType, setConnectionType] = useState('topological');
  const [infraComponentCard, setInfraComponentCard] = useState('stacked');
  const [ringLatency, setRingLatency] = useState(5);
  const [ringColor, setRingColor] = useState('red');
  const [availableRegion, setAvailableRegionState] = useState('all');
  const [metroType, setMetroType] = useState('all');
  const [cloudsList, setCloudsList] = useState(null);
  const [onRamps, setOnRamps] = useState(null);
  const [layers, setLayers] = useState(defaultLayerConfiguration);
  const [onRampConfiguration, setOnRampConfiguration] = useState({
    showHeatMap: true,
    showCount: false,
    showMarketShare: false,
  });
  const [capacityConfiguration, setCapacityConfiguration] = useState(defaultCapacityConfiguration);
  const [mapUrl, setMapUrl] = useState(getMapStyleUrl(getMapStyle()));
  const [selectedMapStyle, setSelectedMapStyle] = useState(getMapStyle());
  const [selectedOnRamps, setSelectedOnRamps] = useState([]);
  const [openMapMenu, setOpenMapMenu] = useState(null);
  const [mapType, setMapType] = useState('heatmap');
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [capOnRamp, setCapOnRamp] = useState('all');
  const [marketplaceSegment, setMarketplaceSegment] = useState(null);
  const [selectedEms, setSelectedEms] = useState(null);

  useEffect(() => {
    if (!metrosData) return;
    setMetros(Metro.fromJSONArray(metrosData.metros));
  }, [metrosData]);

  useEffect(() => {
    if (!onRampsData) return;
    const allOnRamps = OnRamp.fromJSONArray(onRampsData.getOnRampClouds.flat());
    setOnRamps(allOnRamps);
    setCloudsList([...new Set(allOnRamps.map((onRamp) => onRamp.cloud))]);
  }, [onRampsData]);

  useEffect(() => {
    if (!layersData) return;
    setLayers((prevLayers) => ({
      ...prevLayers,
      metroLabels: layersData.getLayersConfiguration.metroLabels,
      connections: layersData.getLayersConfiguration.connections,
      inactiveMetros: layersData.getLayersConfiguration.inactiveMetros,
      latencyRings: layersData.getLayersConfiguration.latencyRings,
      availableRegions: layersData.getLayersConfiguration.availableRegions,
    }));
    setRingLatency(layersData.getLayersConfiguration.ringLatency);
    setRingColor(layersData.getLayersConfiguration.ringColor);
    setConnectionType(layersData.getLayersConfiguration.connectionType);
    setCapOnRamp(layersData.getLayersConfiguration.capOnRamp);
    setOnRampConfiguration({
      showHeatMap: layersData.getLayersConfiguration.showHeatMap,
      showCount: layersData.getLayersConfiguration.showCount,
      showMarketShare: layersData.getLayersConfiguration.showMarketShare,
    });
    setSelectedOnRamps(layersData.getLayersConfiguration.selectedOnRamps);
  }, [layersData]);

  useEffect(() => {
    if (!layersData) return;

    const savedSegmentId = layersData.getLayersConfiguration.marketplaceSegment;
    if (!savedSegmentId) {
      setMarketplaceSegment(null);
      return;
    }

    const matchedSegment = marketplaceSegments.find((segment) => ObjectUtils.equals(segment.id, savedSegmentId)) || null;
    setMarketplaceSegment(matchedSegment);
  }, [layersData, marketplaceSegments]);

  const toggleLayer = useCallback(
    (layer) => (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
    },
    []
  );

  const toggleAvailableRegions = useCallback(
    (layer) => (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      if (!layers[layer]) {
        setAvailableRegionState('all');
        setLayers((prev) => ({
          ...prev,
          availableRegions: true,
          activeRegions: true,
          inactiveRegions: true,
        }));
      } else {
        setLayers((prev) => ({ ...prev, availableRegions: false, activeRegions: false, inactiveRegions: false }));
      }
    },
    [layers]
  );

  const toggleLabel = useCallback(
    (layer) => (e) => {
      if (e && e.stopPropagation) e.stopPropagation();

      setLayers((prev) => {
        const newLayer = { ...prev, [layer]: !prev[layer] };
        if (layer === 'labelSectionEnabled' && !prev.labelSectionEnabled && !prev.labels && !prev.metroLabels && !prev.locationLabels && !prev.regionLabels) {
          return { ...prev, labelSectionEnabled: true, labels: true };
        }
        if (!newLayer.labels && !newLayer.metroLabels && !newLayer.locationLabels && !newLayer.regionLabels && prev.labelSectionEnabled) {
          return { ...newLayer, labelSectionEnabled: false };
        }
        return newLayer;
      });
    },
    []
  );

  const setAvailableRegion = useCallback((region) => {
    if (region === 'all') {
      setAvailableRegionState(region);
      setLayers((prev) => ({ ...prev, activeRegions: true, inactiveRegions: true }));
    } else {
      setAvailableRegionState(region);
      setLayers((prev) => ({ ...prev, activeRegions: true, inactiveRegions: false }));
    }
  }, []);

  return {
    metros,
    connectionType,
    setConnectionType,
    infraComponentCard,
    setInfraComponentCard,
    ringLatency,
    setRingLatency,
    ringColor,
    setRingColor,
    availableRegion,
    setAvailableRegion,
    metroType,
    setMetroType,
    cloudsList,
    onRamps,
    layers,
    setLayers,
    onRampConfiguration,
    setOnRampConfiguration,
    capacityConfiguration,
    setCapacityConfiguration,
    mapUrl,
    setMapUrl,
    selectedMapStyle,
    setSelectedMapStyle,
    selectedOnRamps,
    setSelectedOnRamps,
    openMapMenu,
    setOpenMapMenu,
    mapType,
    setMapType,
    selectedLayer,
    setSelectedLayer,
    capOnRamp,
    setCapOnRamp,
    marketplaceSegment,
    setMarketplaceSegment,
    selectedEms,
    setSelectedEms,
    toggleLayer,
    toggleAvailableRegions,
    toggleLabel,
    defaultLayerConfiguration,
    defaultCapacityConfiguration,
  };
};
